import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ErrorSummary, type SummaryIssue } from '../../job-roles/components/ErrorSummary';
import { ModalDialog } from '../../job-roles/components/ModalDialog';
import { bdErrorMessage, errorCode, isForbidden, isVersionConflict } from '../bdErrors';
import { ANNUAL_BONUS_NOTE, ANNUAL_BONUS_PLAN_CODE } from '../catalogData';
import { BENEFIT_CATEGORY_CODES, BENEFIT_CATEGORY_LABELS, type BenefitCategoryCode } from '../categories';
import { declaredTaxFormToInput, declaredTaxIssues, emptyDeclaredTaxForm, hasDeclaredTaxIssues, inlineCreateIssues, type DeclaredTaxFormState } from '../declaredTaxProfile';
import { BENEFIT_FORM_WARNINGS } from '../copy';
import { benefitsGateway, deductionsGateway } from '../index';
import {
  COVERAGE_LEVEL_CHOICES, applyCatalogType, emptyPlanForm, formToInput, planToForm, suggestShortCode, visibleAmountFields, type PlanFormState,
} from '../planForm';
import { BdErrorCode } from '../types';
import {
  CONTRIBUTION_METHODS, ENROLLMENT_METHODS, FUNDING_MODELS, PAYROLL_FREQUENCIES, contributionMethodLabels, coverageLevelLabels, enrollmentMethodLabels, fundingModelLabels,
  payrollFrequencyLabels, type BenefitCatalogItem, type BenefitPlan, type DeductionDefinition, type TaxProfile,
} from '../types';
import { DeclaredTaxFields } from './DeclaredTaxFields';
import { RenderSlot } from './RenderSlot';
import { StatusBadge } from './ListControls';
import { CheckboxInput, RelatedSelect, SelectInput, TextAreaInput, TextInput } from './fields';
import '../../job-roles/job-roles.css';
import '../benefits-deductions.css';

export type PlanDialogMode = 'create' | 'edit' | 'view';

export interface PlanFormDialogProps {
  mode: PlanDialogMode;
  /** Create: the page's category. */
  category: BenefitCategoryCode;
  /** Create from the Overview: lets the person choose any category. Omitted on a category page, where the category is fixed. */
  categoryChoices?: BenefitCategoryCode[];
  /** Create from Employee Profile Setup: the tax treatment is required, and declared here as "needs review" (contract 4.1.1.1). Create only. */
  requireDeclaredTax?: boolean;
  /** Create: start from this catalog type. Null or omitted starts a custom benefit. */
  catalogType?: BenefitCatalogItem | null;
  planId?: string;
  onClose: () => void;
  onSaved: (plan: BenefitPlan, mode: 'create' | 'edit') => void;
  /** A write came back 403: the page turns read-only. */
  onForbidden: () => void;
  /** The canonical "Add a deduction" dialog, supplied by the page so the person can create the deduction in place without losing this form. */
  renderCreateDeduction?: (props: { onCreated: (definition: DeductionDefinition) => void; onClose: () => void }) => ReactNode;
}

type Load = { kind: 'loading' } | { kind: 'ready'; plan: BenefitPlan | null } | { kind: 'error'; message: string };

const idOf = (field: string) => `bd-plan-${field.split('.')[0]}`;

export function PlanFormDialog({ mode, category, categoryChoices, requireDeclaredTax, catalogType, planId, onClose, onSaved, onForbidden, renderCreateDeduction }: PlanFormDialogProps) {
  const readOnlyView = mode === 'view';
  const [load, setLoad] = useState<Load>(mode === 'create' ? { kind: 'ready', plan: null } : { kind: 'loading' });
  const [form, setForm] = useState<PlanFormState>(() => {
    const base = emptyPlanForm(catalogType?.categoryCode ?? category);
    return catalogType ? { ...applyCatalogType(base, catalogType), shortCode: suggestShortCode(catalogType.name) } : base;
  });
  const [shortCodeTouched, setShortCodeTouched] = useState(false);
  const needsDeclared = requireDeclaredTax === true && mode === 'create';
  const [declared, setDeclared] = useState<DeclaredTaxFormState>(emptyDeclaredTaxForm);
  const [declaredAttempted, setDeclaredAttempted] = useState(false);
  const [catalog, setCatalog] = useState<BenefitCatalogItem[]>([]);
  const [profiles, setProfiles] = useState<TaxProfile[]>([]);
  const [definitions, setDefinitions] = useState<DeductionDefinition[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [summary, setSummary] = useState<SummaryIssue[]>([]);
  const [formError, setFormError] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState<'deduction' | 'employer' | null>(null);
  const [reloadNonce, setReloadNonce] = useState(0);
  // Which select to focus once the nested dialog has closed and returned focus to its opener (an effect, so it runs after that).
  const focusAfter = useRef<'deduction' | 'employer' | null>(null);
  const summaryRef = useRef<HTMLDivElement>(null);
  const deductionSelectRef = useRef<HTMLSelectElement>(null);
  const employerSelectRef = useRef<HTMLSelectElement>(null);

  // The plan being edited or viewed.
  useEffect(() => {
    if (mode === 'create' || !planId) return;
    let cancelled = false;
    benefitsGateway.getPlan(planId)
      .then((plan) => { if (!cancelled) { setForm(planToForm(plan)); setShortCodeTouched(true); setLoad({ kind: 'ready', plan }); } })
      .catch((err: unknown) => { if (!cancelled) { if (isForbidden(err)) onForbidden(); setLoad({ kind: 'error', message: bdErrorMessage(err, 'Could not load this plan.') }); } });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, planId, reloadNonce]);

  // The catalog types of the chosen category, for the "Benefit type" choice.
  useEffect(() => {
    let cancelled = false;
    benefitsGateway.listCatalog({ category: form.categoryCode })
      .then((items) => { if (!cancelled) setCatalog(items); })
      .catch(() => { if (!cancelled) setCatalog([]); });
    return () => { cancelled = true; };
  }, [form.categoryCode]);

  // Approved tax profiles (read only: the company cannot create one) and the deductions a plan can point at.
  useEffect(() => {
    let cancelled = false;
    deductionsGateway.listTaxProfiles().then((p) => { if (!cancelled) setProfiles(p); }).catch(() => undefined);
    deductionsGateway.listDefinitions({ pageSize: 100 }).then((d) => { if (!cancelled) setDefinitions(d.items); }).catch(() => undefined);
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (focusAfter.current === null || creating !== null) return;
    (focusAfter.current === 'deduction' ? deductionSelectRef : employerSelectRef).current?.focus();
    focusAfter.current = null;
  }, [creating]);

  const plan = load.kind === 'ready' ? load.plan : null;
  const disabled = readOnlyView || busy;
  const set = <K extends keyof PlanFormState>(key: K, value: PlanFormState[K]) => setForm((f) => ({ ...f, [key]: value }));
  const show = visibleAmountFields(form);
  const leave = form.categoryCode === 'leave-wellbeing';
  const bonus = form.typeCode === ANNUAL_BONUS_PLAN_CODE;
  const hasElections = (plan?.enrolledCount ?? 0) > 0;
  const lockMethod = leave || bonus || (mode === 'edit' && hasElections);
  const lockFunding = leave;
  const selectedType = catalog.find((c) => c.code === form.typeCode);

  function changeCategory(next: BenefitCategoryCode) {
    setForm((f) => ({
      ...f, categoryCode: next, typeCode: '',
      ...(next === 'leave-wellbeing' ? { fundingModel: 'NotApplicable' as const, contributionMethod: 'NoPayrollContribution' as const, enrollmentMethod: 'NotApplicable' as const, tiers: [] } : {}),
    }));
  }

  function changeType(code: string) {
    if (code === '') { set('typeCode', ''); return; }
    const item = catalog.find((c) => c.code === code);
    if (!item) return;
    setForm((f) => {
      const next = applyCatalogType(f, item);
      return shortCodeTouched ? { ...next, shortCode: f.shortCode } : { ...next, shortCode: suggestShortCode(item.name) };
    });
  }

  function changeName(value: string) {
    setForm((f) => ({ ...f, name: value, ...(shortCodeTouched ? {} : { shortCode: suggestShortCode(value) }) }));
  }

  function changeMethod(value: PlanFormState['contributionMethod']) {
    setForm((f) => ({
      ...f, contributionMethod: value,
      tiers: value === 'TieredByCoverage' && f.tiers.length === 0 ? [{ coverageLevel: 'EmployeeOnly', employeeAmount: '', employerAmount: '' }] : f.tiers,
    }));
  }

  function showSummary(list: SummaryIssue[]) {
    setSummary(list);
    setTimeout(() => summaryRef.current?.focus(), 0);
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (busy || readOnlyView) return;
    setFormError(null);
    setConflict(false);
    const { input, issues: formIssues } = formToInput(form);
    const issues = needsDeclared ? [...formIssues, ...inlineCreateIssues(form)] : formIssues;
    const declaredProblem = needsDeclared ? declaredTaxIssues(declared, true) : null;
    const declaredBlocked = declaredProblem !== null && hasDeclaredTaxIssues(declaredProblem);
    if (issues.length > 0 || declaredBlocked) {
      const byField: Record<string, string> = {};
      for (const issue of issues) if (!byField[issue.field]) byField[issue.field] = issue.message;
      setErrors(byField);
      setDeclaredAttempted(true);
      const list = Object.entries(byField).map(([field, message]) => ({ key: idOf(field), message }));
      if (declaredBlocked) list.push({ key: 'bd-plan-declared', message: declaredProblem.treatment ?? declaredProblem.cells ?? 'Choose the tax treatment.' });
      showSummary(list);
      return;
    }
    setErrors({});
    setSummary([]);
    setBusy(true);
    try {
      const saved = mode === 'create'
        ? await benefitsGateway.createPlan(needsDeclared ? { ...input, declaredTaxProfile: declaredTaxFormToInput(declared) } : input)
        : await benefitsGateway.updatePlan(planId!, { ...input, expectedVersion: plan!.concurrencyVersion });
      onSaved(saved, mode === 'create' ? 'create' : 'edit');
    } catch (err) {
      if (isForbidden(err)) onForbidden();
      if (isVersionConflict(err)) setConflict(true);
      else if (errorCode(err) === BdErrorCode.DuplicateName) setErrors({ name: bdErrorMessage(err, '') });
      else if (errorCode(err) === BdErrorCode.DuplicateCode) setErrors({ shortCode: bdErrorMessage(err, '') });
      else setFormError(bdErrorMessage(err, mode === 'create' ? 'Could not add this benefit.' : 'Could not save your changes.'));
      setBusy(false);
    }
  }

  function handleDeductionCreated(definition: DeductionDefinition) {
    setDefinitions((list) => (list.some((d) => d.id === definition.id) ? list : [...list, definition]));
    if (creating === 'deduction') set('associatedDeductionCodeId', definition.id); else set('associatedEmployerContributionCodeId', definition.id);
    focusAfter.current = creating;
    setCreating(null);
  }

  const title = mode === 'create'
    ? (catalogType && catalogType.code === form.typeCode && form.typeCode !== '' ? `Add ${catalogType.name}` : categoryChoices ? 'Add Benefit' : 'Add Custom Benefit')
    : mode === 'edit' ? `Edit ${plan?.name ?? 'benefit plan'}` : `View ${plan?.name ?? 'benefit plan'}`;

  const categoryOptions = (categoryChoices ?? BENEFIT_CATEGORY_CODES.filter((c) => c === category)).map((c) => ({ value: c, label: BENEFIT_CATEGORY_LABELS[c] }));
  const typeOptions = [{ value: '', label: 'Custom benefit (not from the catalog)' }, ...catalog.map((c) => ({ value: c.code, label: c.name }))];
  const profileOptions = [{ value: '', label: 'None yet (tax review required)' }, ...profiles.map((p) => ({ value: p.code, label: p.name }))];
  const definitionOptions = (selected: string) => [
    { value: '', label: 'None' },
    ...definitions.map((d) => ({ value: d.id, label: d.name })),
    ...(selected && !definitions.some((d) => d.id === selected) ? [{ value: selected, label: 'Linked deduction' }] : []),
  ];

  const fieldError = (field: string) => errors[field];
  const methodOptions = CONTRIBUTION_METHODS.map((m) => ({ value: m, label: contributionMethodLabels[m] }));

  return (
    <>
      <ModalDialog
        title={title}
        size="lg"
        busy={busy}
        onClose={onClose}
        footer={(
          <>
            <button type="button" className="jr-btn jr-btn--secondary" onClick={onClose} disabled={busy}>{readOnlyView ? 'Close' : 'Cancel'}</button>
            {!readOnlyView && load.kind === 'ready' && (
              <button type="submit" form="bd-plan-form" className="jr-btn jr-btn--primary" disabled={busy}>
                {busy ? 'Saving…' : mode === 'create' ? 'Add benefit' : 'Save changes'}
              </button>
            )}
          </>
        )}
      >
        {load.kind === 'loading' && <p className="muted" role="status">Loading plan{'…'}</p>}
        {load.kind === 'error' && (
          <div role="alert">
            <p className="jr-error">{load.message}</p>
            <button type="button" className="jr-btn jr-btn--secondary" onClick={() => { setLoad({ kind: 'loading' }); setReloadNonce((n) => n + 1); }}>Try again</button>
          </div>
        )}
        {load.kind === 'ready' && (
          <form id="bd-plan-form" className="bd-form" onSubmit={(e) => void submit(e)} noValidate>
            <div className="bd-warnings" role="note" aria-label="Before you continue">
              <ul>{BENEFIT_FORM_WARNINGS.map((w) => <li key={w}>{w}</li>)}</ul>
            </div>
            <ErrorSummary ref={summaryRef} issues={summary} onSelect={(key) => document.getElementById(key)?.focus()} />
            {formError && <p className="jr-error jr-error--block" role="alert">{formError}</p>}
            {conflict && (
              <div className="jr-conflict" role="alert">
                <p>This plan was changed by someone else after you opened it, so your changes were not saved. Reload the latest version, then make your changes again.</p>
                <button type="button" className="jr-btn jr-btn--secondary jr-btn--small" onClick={() => { setConflict(false); setLoad({ kind: 'loading' }); setReloadNonce((n) => n + 1); }}>Reload latest</button>
              </div>
            )}
            {plan && (
              <div className="bd-status-line">
                <StatusBadge isActive={plan.isActive} configurationStatus={plan.configurationStatus} />
                <span className="muted">{plan.enrolledCount} {plan.enrolledCount === 1 ? 'employee' : 'employees'} enrolled</span>
              </div>
            )}
            {plan && plan.configurationIssues.length > 0 && (
              <div className="bd-notice bd-notice--warn" role="status">
                <p><strong>What needs attention</strong></p>
                <ul className="bd-list">{plan.configurationIssues.map((i) => <li key={i}>{i}</li>)}</ul>
              </div>
            )}
            {leave && (
              <p className="bd-notice bd-notice--info" role="note">
                Leave balances and accruals are managed in Time and Leave. A leave benefit here only describes the program: it carries no payroll contribution.{' '}
                <Link to="/time-off">Open Time Off</Link>
              </p>
            )}
            {bonus && <p className="bd-notice bd-notice--info" role="note">{ANNUAL_BONUS_NOTE}</p>}
            {selectedType && selectedType.createsPayrollDeduction !== 'Never' && !leave && (
              <p className="bd-help">This type {selectedType.createsPayrollDeduction === 'Typical' ? 'usually' : 'may'} have a payroll deduction. Choose a contribution method below to set it up.</p>
            )}

            <fieldset className="bd-fieldset" disabled={disabled}>
              <legend>About this benefit</legend>
              <div className="jr-grid">
                <SelectInput
                  id="bd-plan-categoryCode" label="Category" required value={form.categoryCode} onChange={(v) => changeCategory(v as BenefitCategoryCode)}
                  options={categoryOptions} disabled={disabled || mode !== 'create' || !categoryChoices} error={fieldError('categoryCode')}
                  hint={mode === 'edit' && hasElections ? 'A plan that has elections cannot change its category.' : undefined}
                  help="The group this benefit belongs to. It decides which page lists the plan."
                />
                <SelectInput
                  id="bd-plan-typeCode" label="Benefit type" value={form.typeCode} onChange={changeType} options={typeOptions} disabled={disabled || mode !== 'create'}
                  help="Start from a type in the catalog, which fills in the name and description, or create a custom benefit."
                />
                <TextInput id="bd-plan-name" label="Name" required value={form.name} onChange={changeName} error={fieldError('name')} maxLength={120} autoFocus />
                <TextInput
                  id="bd-plan-shortCode" label="Short code" required value={form.shortCode} onChange={(v) => { setShortCodeTouched(true); set('shortCode', v); }} error={fieldError('shortCode')}
                  hint="2 to 30 letters, numbers, hyphens or underscores." maxLength={30} help="A short label used in lists and reports. It must be different from every other plan's code."
                />
                <TextAreaInput id="bd-plan-description" label="Description" required={needsDeclared || undefined} value={form.description} onChange={(v) => set('description', v)} maxLength={500} />
                <TextInput id="bd-plan-providerName" label="Provider name" value={form.providerName} onChange={(v) => set('providerName', v)} maxLength={120} help="The carrier or administrator named in the plan documents. Use a company name only, never a policy number or contact person." />
                <TextInput id="bd-plan-planYear" label="Plan year" value={form.planYear} onChange={(v) => set('planYear', v)} error={fieldError('planYear')} inputMode="numeric" hint="From 2000 to 2100." help="The year the plan's rates and rules apply to." />
                <TextInput id="bd-plan-effectiveStartDate" label="Effective start date" required={needsDeclared || undefined} type="date" value={form.effectiveStartDate} onChange={(v) => set('effectiveStartDate', v)} help="Employees can only be enrolled on or after this date. Leave it empty if the plan has no start date." />
                <TextInput id="bd-plan-effectiveEndDate" label="Effective end date" type="date" value={form.effectiveEndDate} onChange={(v) => set('effectiveEndDate', v)} error={fieldError('effectiveEndDate')} help="Employees can only be enrolled on or before this date. Leave it empty if the plan does not end." />
                <TextInput id="bd-plan-planDocumentReference" label="Plan document reference" value={form.planDocumentReference} onChange={(v) => set('planDocumentReference', v)} maxLength={200} help="A note naming where the plan document is kept. EPN does not store the document itself." />
              </div>
            </fieldset>

            <fieldset className="bd-fieldset" disabled={disabled}>
              <legend>Eligibility and enrollment</legend>
              <p className="bd-help">Phase 1 treats every active employee as eligible. These fields describe your rules; they do not block anyone yet.</p>
              <div className="jr-grid">
                <TextAreaInput id="bd-plan-eligibilityDescription" label="Who is eligible" value={form.eligibilityDescription} onChange={(v) => set('eligibilityDescription', v)} maxLength={500} />
                <TextInput id="bd-plan-waitingPeriodDays" label="Waiting period (days)" value={form.waitingPeriodDays} onChange={(v) => set('waitingPeriodDays', v)} error={fieldError('waitingPeriodDays')} inputMode="numeric" hint="From 0 to 365." help="How many days after hire an employee must wait before enrolling." />
                <SelectInput
                  id="bd-plan-enrollmentMethod" label="How employees enroll" value={form.enrollmentMethod} onChange={(v) => set('enrollmentMethod', v as PlanFormState['enrollmentMethod'])}
                  options={ENROLLMENT_METHODS.map((m) => ({ value: m, label: enrollmentMethodLabels[m] }))}
                  help="Who starts the enrollment. This setting does not enroll an employee with an insurance carrier."
                />
              </div>
            </fieldset>

            <fieldset className="bd-fieldset" disabled={disabled}>
              <legend>Cost and payroll contribution</legend>
              <div className="jr-grid">
                <SelectInput
                  id="bd-plan-fundingModel" label="Who pays" value={form.fundingModel} onChange={(v) => set('fundingModel', v as PlanFormState['fundingModel'])} disabled={disabled || lockFunding}
                  options={FUNDING_MODELS.map((m) => ({ value: m, label: fundingModelLabels[m] }))} error={fieldError('fundingModel')}
                  help="Who pays for this benefit: the employer, the employee, both, or no one (for example a leave program). An employer-paid benefit takes nothing from the employee's pay."
                />
                <SelectInput
                  id="bd-plan-contributionMethod" label="How the amount is worked out" value={form.contributionMethod} onChange={(v) => changeMethod(v as PlanFormState['contributionMethod'])}
                  options={methodOptions} disabled={disabled || lockMethod} error={fieldError('contributionMethod')}
                  hint={mode === 'edit' && hasElections ? 'A plan that has elections cannot change its contribution method.' : undefined}
                  help="How the money is worked out on a pay statement. Choose 'No payroll contribution' when nothing is taken from pay for this benefit. Percentages are calculated at payroll."
                />
                {show.employeeAmount && <TextInput id="bd-plan-employeeContributionAmount" label="Employee amount" value={form.employeeContributionAmount} onChange={(v) => set('employeeContributionAmount', v)} error={fieldError('employeeContributionAmount')} inputMode="decimal" hint="Dollars each pay period or month, from 0 to 1,000,000." />}
                {show.employerAmount && <TextInput id="bd-plan-employerContributionAmount" label="Employer amount" value={form.employerContributionAmount} onChange={(v) => set('employerContributionAmount', v)} error={fieldError('employerContributionAmount')} inputMode="decimal" hint="Dollars each pay period or month, from 0 to 1,000,000." />}
                {show.employeePercent && <TextInput id="bd-plan-employeeContributionPercent" label="Employee percent" value={form.employeeContributionPercent} onChange={(v) => set('employeeContributionPercent', v)} error={fieldError('employeeContributionPercent')} inputMode="decimal" hint="From 0 to 100. Calculated at payroll." />}
                {show.employerPercent && <TextInput id="bd-plan-employerContributionPercent" label="Employer percent" value={form.employerContributionPercent} onChange={(v) => set('employerContributionPercent', v)} error={fieldError('employerContributionPercent')} inputMode="decimal" hint="From 0 to 100. Calculated at payroll." />}
                <SelectInput
                  id="bd-plan-payrollFrequency" label="How often it is taken" value={form.payrollFrequency} onChange={(v) => set('payrollFrequency', v as PlanFormState['payrollFrequency'])}
                  options={PAYROLL_FREQUENCIES.map((f) => ({ value: f, label: payrollFrequencyLabels[f] }))} help="How often the contribution is taken from pay. Until payroll supports other schedules, a deduction follows the employee's pay schedule."
                />
              </div>
              {show.tiers && (
                <div id="bd-plan-coverageTiers" tabIndex={-1} className="bd-tiers">
                  <p className="bd-help">Add one row for each coverage level the plan offers, each with its own amount. A coverage level can appear only once.</p>
                  {fieldError('coverageTiers') && <p className="jr-error" role="alert">{fieldError('coverageTiers')}</p>}
                  {form.tiers.map((tier, index) => (
                    <div className="bd-tier-row" key={index}>
                      <SelectInput
                        id={`bd-plan-tier-${index}-level`} label="Coverage level" value={tier.coverageLevel}
                        onChange={(v) => setForm((f) => ({ ...f, tiers: f.tiers.map((t, i) => (i === index ? { ...t, coverageLevel: v as typeof tier.coverageLevel } : t)) }))}
                        options={COVERAGE_LEVEL_CHOICES.map((c) => ({ value: c, label: coverageLevelLabels[c] }))}
                      />
                      {show.employeeTier && (
                        <TextInput
                          id={`bd-plan-tier-${index}-employee`} label="Employee amount" value={tier.employeeAmount} inputMode="decimal" error={errors[`coverageTiers.${index}`]}
                          onChange={(v) => setForm((f) => ({ ...f, tiers: f.tiers.map((t, i) => (i === index ? { ...t, employeeAmount: v } : t)) }))}
                        />
                      )}
                      {show.employerTier && (
                        <TextInput
                          id={`bd-plan-tier-${index}-employer`} label="Employer amount" value={tier.employerAmount} inputMode="decimal"
                          onChange={(v) => setForm((f) => ({ ...f, tiers: f.tiers.map((t, i) => (i === index ? { ...t, employerAmount: v } : t)) }))}
                        />
                      )}
                      <button type="button" className="jr-btn jr-btn--secondary jr-btn--small" aria-label={`Remove coverage tier ${index + 1}`} onClick={() => setForm((f) => ({ ...f, tiers: f.tiers.filter((_, i) => i !== index) }))}>Remove</button>
                    </div>
                  ))}
                  <button type="button" className="jr-btn jr-btn--secondary jr-btn--small" disabled={disabled || form.tiers.length >= COVERAGE_LEVEL_CHOICES.length}
                    onClick={() => setForm((f) => {
                      const used = new Set(f.tiers.map((t) => t.coverageLevel));
                      const next = COVERAGE_LEVEL_CHOICES.find((c) => !used.has(c)) ?? 'EmployeeOnly';
                      return { ...f, tiers: [...f.tiers, { coverageLevel: next, employeeAmount: '', employerAmount: '' }] };
                    })}>
                    <span aria-hidden="true">+ </span>Add coverage tier
                  </button>
                </div>
              )}
            </fieldset>

            <fieldset className="bd-fieldset" disabled={disabled}>
              <legend>Payroll setup and tax</legend>
              <div className="jr-grid">
                {needsDeclared && <DeclaredTaxFields state={declared} onChange={setDeclared} issues={declaredTaxIssues(declared, true)} showIssues={declaredAttempted} disabled={disabled} idPrefix="bd-plan-declared" />}
                {form.contributionMethod !== 'NoPayrollContribution' && (
                  <SelectInput
                    id="bd-plan-taxProfileCode" label="Tax profile" value={form.taxProfileCode} onChange={(v) => set('taxProfileCode', v)} options={profileOptions} error={fieldError('taxProfileCode')}
                    hint={profiles.length === 0 ? 'No tax profile is approved for payroll yet. Tax profiles are managed by Escochex, not by your company. Until one is approved, this plan stays "Configuration incomplete: tax review required" and employees cannot be enrolled in it.' : 'Tax profiles are managed by Escochex. Only profiles approved for payroll can be chosen.'}
                    help="A tax profile says how the amount affects taxable wages. Tax treatment must be approved before this deduction can be used in payroll."
                  />
                )}
                <TextInput id="bd-plan-w2ReportingCode" label="W-2 reporting code" value={form.w2ReportingCode} onChange={(v) => set('w2ReportingCode', v)} maxLength={10} help="Tells payroll which W-2 box this amount is reported in, when one applies. Leave it empty if you are not sure." />
                {form.contributionMethod !== 'NoPayrollContribution' && (
                  <RelatedSelect
                    ref={deductionSelectRef} id="bd-plan-associatedDeductionCodeId" label="Payroll deduction for the employee's share" value={form.associatedDeductionCodeId}
                    onChange={(v) => set('associatedDeductionCodeId', v)} options={definitionOptions(form.associatedDeductionCodeId)}
                    help="The deduction that takes the employee's share from pay. Selecting a benefit does not automatically create a payroll deduction."
                    onAdd={renderCreateDeduction && !disabled ? () => setCreating('deduction') : undefined} addLabel="Add a deduction"
                    noAddNote="Ask an administrator to add a deduction if the one you need is missing."
                  />
                )}
                {form.contributionMethod !== 'NoPayrollContribution' && form.fundingModel !== 'EmployeePaid' && (
                  <RelatedSelect
                    ref={employerSelectRef} id="bd-plan-associatedEmployerContributionCodeId" label="Payroll code for the employer's share" value={form.associatedEmployerContributionCodeId}
                    onChange={(v) => set('associatedEmployerContributionCodeId', v)} options={definitionOptions(form.associatedEmployerContributionCodeId)}
                    help="The code that records the employer's share. It is a record only: nothing is paid to a carrier from here."
                    onAdd={renderCreateDeduction && !disabled ? () => setCreating('employer') : undefined} addLabel="Add a deduction"
                    noAddNote="Ask an administrator to add a deduction if the one you need is missing."
                  />
                )}
              </div>
            </fieldset>

            <fieldset className="bd-fieldset" disabled={disabled}>
              <legend>Visibility and notes</legend>
              <div className="jr-grid">
                <TextAreaInput id="bd-plan-employeeVisibleDescription" label="Description employees see" value={form.employeeVisibleDescription} onChange={(v) => set('employeeVisibleDescription', v)} maxLength={500} help="Shown to employees. Write it in plain language." />
                <TextAreaInput id="bd-plan-internalNotes" label="Internal notes" value={form.internalNotes} onChange={(v) => set('internalNotes', v)} maxLength={1000} hint="Only administrators see this. Do not enter health information, policy numbers or other personal data." />
                <CheckboxInput id="bd-plan-isActive" label="Plan is active" checked={form.isActive} onChange={(v) => set('isActive', v)} hint="An inactive plan stays in the list but cannot take new elections." />
              </div>
            </fieldset>
          </form>
        )}
      </ModalDialog>
      {creating && renderCreateDeduction && (
        <RenderSlot render={renderCreateDeduction} props={{ onCreated: handleDeductionCreated, onClose: () => setCreating(null) }} />
      )}
    </>
  );
}

